// JOBE-0165.1: W5 PromptOps Agent — single-agent layout, identity head, quality strip.
//
// Replaces the JOBE-0159.4 inline stub in main.jsx. Sub-nav mount + identity
// head (reused from JOBE-0161's AgentDetail) + 5-tile quality strip.
//
// JOBE-0165.2: prompt-version stack + shared ActionConfirmModal.
// JOBE-0165.3: failure cluster cards + drill breadcrumb + ?failureClass= scroll-into-view.
// JOBE-0165.4: eval results panel + candidate fix bundle card.
// JOBE-0165.5: keyboard shortcut bar + page-local hotkey listener + OmniChord additions.

import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { PromptOpsSubNav } from '../components/PromptOpsSubNav/PromptOpsSubNav.jsx';
import { IdentityHead } from '../AgentDetail/AgentDetail.jsx';
import { KpiTile } from '../components/KpiTile/KpiTile.jsx';
import { Spark } from '../components/Spark/Spark.jsx';
import { Card } from '../components/Card/Card.jsx';
import { StatusPill } from '../components/StatusPill/StatusPill.jsx';
import { flaggedFetch } from '../lib/flags.js';
import { useFlagStore } from '../lib/useFlagStore.js';
import { toneForRate, toneForDeviation } from '../lib/kpiTone.js';
import { useDrillContext, clearDrillContext } from '../lib/drillContext.js';
import '../mock-wcc.js'; // registers fixtures with FIXTURES map
import './PromptOpsAgentPage.css';

// ---- slugify (JOBE-0165.3) ----
//
// Used both for the `data-cluster-slug` attribute and the `?failureClass=`
// query-param comparison. EXPORTED so JOBE-0164 (W6 PromptOps Fleet) can
// import the same function and stay drift-free when constructing the
// drill-down URL.
export function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Small title-case helper used by the breadcrumb (mirrors AgentDetail's
// private toTitleCase — kept local rather than exported because the W2
// version is a single-line lambda).
function toTitleCase(s) {
  if (!s) return '';
  return String(s).replace(/(\S+)/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// ---- relativeTime (JOBE-0165.4) ----
//
// Local helper for "Xs/m/h/d ago" rendering. Kept page-local rather than
// extracted to a shared lib per the parent spec's "no new shared components"
// guidance — if a third surface needs the same format later, extracting is
// cheap.
export function relativeTime(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60)     return `${s}s ago`;
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ---- bundleId / computeBundle (JOBE-0165.4) ----
//
// Bundle composition is v1 client-side only: every fixture cluster goes in
// the bundle. A real lift model would consult the eval database — out of
// scope for v1; the heuristic below is a documented demo number.
export function bundleId(clusters) {
  const seed = clusters.map((c) => c.cls).join('|');
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) % (36 ** 4);
  return `bndl-${h.toString(36).padStart(4, '0')}`;
}

export function computeBundle(clusters) {
  const totalTraces = clusters.reduce((sum, c) => sum + (c.count || 0), 0);
  // Heuristic: expected lift = totalTraces / 100, capped at 0.15. Demo
  // number — a real lift model would draw on historical correction →
  // pass-rate-delta correlations from the eval database.
  const expectedLift = Math.min(totalTraces / 100, 0.15);
  return {
    id: bundleId(clusters),
    clusters,
    expectedLift,
  };
}

// ---- QualityStrip ----
//
// 5 KPI tiles in a single grid row, absolute-mode sparks with target lines
// per D11 (sparks in absolute mode have target lines, not deviation midlines).
// Empty state renders 5 dashed tiles so the grid keeps its shape during the
// real-endpoint fetch (which currently always fails — see TODO below).
function QualityStrip({ data }) {
  if (!data) {
    return (
      <div className="promptops-quality-strip" data-testid="quality-strip-empty">
        {[1, 2, 3, 4, 5].map((i) => (
          <KpiTile key={i} label="—" value="—" />
        ))}
      </div>
    );
  }
  return (
    <div className="promptops-quality-strip">
      <KpiTile
        label="PASS RATE"
        value={`${(data.evalPassRate * 100).toFixed(0)}%`}
        tone={toneForRate(data.evalPassRate, 0.90, 'above-ok')}
        spark={
          <Spark
            mode="absolute"
            data={data.evalPassRateHistory}
            target={0.90}
            tone={toneForRate(data.evalPassRate, 0.90, 'above-ok')}
            ariaLabel="Pass rate trend"
          />
        }
      />
      <KpiTile
        label="TASK COUNT"
        value={String(data.taskCount)}
        tone={toneForDeviation(data.taskCountHistory)}
        spark={
          <Spark
            mode="absolute"
            data={data.taskCountHistory}
            tone={toneForDeviation(data.taskCountHistory)}
            ariaLabel="Task count trend"
          />
        }
      />
      <KpiTile
        label="P50 LATENCY"
        value={String(data.p50LatencyMs)}
        unit="ms"
        tone={toneForRate(data.p50LatencyMs, 1000, 'below-ok')}
        spark={
          <Spark
            mode="absolute"
            data={data.p50LatencyMsHistory}
            target={1000}
            tone={toneForRate(data.p50LatencyMs, 1000, 'below-ok')}
            ariaLabel="p50 latency trend"
          />
        }
      />
      <KpiTile
        label="ERROR RATE"
        value={`${(data.errorRate * 100).toFixed(1)}%`}
        tone={toneForRate(data.errorRate, 0.05, 'below-ok')}
        spark={
          <Spark
            mode="absolute"
            data={data.errorRateHistory}
            target={0.05}
            tone={toneForRate(data.errorRate, 0.05, 'below-ok')}
            ariaLabel="Error rate trend"
          />
        }
      />
      <KpiTile
        label="TOKEN COST"
        value={`$${data.tokenCost.toFixed(2)}`}
        tone={toneForDeviation(data.tokenCostHistory)}
        spark={
          <Spark
            mode="absolute"
            data={data.tokenCostHistory}
            tone={toneForDeviation(data.tokenCostHistory)}
            ariaLabel="Token cost trend"
          />
        }
      />
    </div>
  );
}

// ---- ActionConfirmModal (JOBE-0165.2 — shared with .3 and .4) ----
//
// Preview-only modal. Body text "This is a preview. No request will be sent
// in v1." is a deliberate user-facing notice. A fetch spy in tests must
// observe zero network calls when the modal opens — the modal never submits
// anything in v1.
export function ActionConfirmModal({ action, payload, onClose }) {
  if (!action) return null;
  const titles = {
    'view-diff':        'View diff',
    'promote':          'Promote candidate',
    'discard':          'Discard candidate',
    'apply-correction': 'Apply correction',
    'dismiss-cluster':  'Dismiss cluster',
    'open-trace':       'Open trace in inspector',
    'promote-bundle':   'Promote fix bundle',
  };
  return (
    <div
      className="promptops-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="promptops-modal-title"
      onClick={onClose}
    >
      <div className="promptops-modal" onClick={(e) => e.stopPropagation()}>
        <h2 id="promptops-modal-title">{titles[action] || action}</h2>
        <p className="promptops-modal-preview">
          This is a preview. No request will be sent in v1.
        </p>
        <pre className="promptops-modal-payload">
          {JSON.stringify(payload, null, 2)}
        </pre>
        <footer>
          <button type="button" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}

// ---- VersionCard (JOBE-0165.2) ----
//
// Stacked card showing one prompt version row. Active has no actions;
// candidate exposes View diff / Promote / Discard, all preview-only via the
// shared ActionConfirmModal.
function VersionCard({
  identifier,
  stamp,
  commitHash,
  author,
  diffSummary,
  compileStatus,
  evalStatus,
  actions = [],
  openModal,
}) {
  // D12: prompt-version identifier is a config identifier; must be lowercase.
  // Dev-mode warn if a fixture contributor accidentally uppercases the name.
  if (typeof process !== 'undefined'
      && process.env
      && process.env.NODE_ENV !== 'production'
      && identifier !== identifier.toLowerCase()) {
    // eslint-disable-next-line no-console
    console.warn('[D12] prompt-version identifier should be lowercase:', identifier);
  }
  return (
    <Card className="promptops-version-card">
      <header className="promptops-version-head">
        <span className="promptops-version-identifier">{identifier}</span>
        <StatusPill tone={stamp === 'active' ? 'ok' : 'warn'} label={stamp} />
      </header>
      <dl className="promptops-version-meta">
        <dt>commit</dt><dd><code>{commitHash}</code></dd>
        <dt>author</dt><dd>{author}</dd>
        <dt>diff</dt><dd>{diffSummary}</dd>
        <dt>compile</dt><dd>{compileStatus}</dd>
        <dt>eval</dt><dd>{evalStatus}</dd>
      </dl>
      {actions.length > 0 && (
        <footer className="promptops-version-actions">
          {actions.includes('view-diff') && (
            <button type="button" onClick={() => openModal('view-diff', { identifier })}>
              View diff
            </button>
          )}
          {actions.includes('promote') && (
            <button type="button" onClick={() => openModal('promote', { identifier })}>
              Promote
            </button>
          )}
          {actions.includes('discard') && (
            <button type="button" onClick={() => openModal('discard', { identifier })}>
              Discard
            </button>
          )}
        </footer>
      )}
    </Card>
  );
}

// ---- PromptVersionStack (JOBE-0165.2) ----
function PromptVersionStack({ data, openModal }) {
  if (!data) return null;
  return (
    <section className="promptops-version-stack" aria-label="Prompt versions">
      <VersionCard
        identifier={`${data.name}@${data.version}`}
        stamp="active"
        commitHash={data.activeCommit}
        author={data.activeAuthor}
        diffSummary={data.activeDiffSummary}
        compileStatus={data.activeCompileStatus}
        evalStatus={data.activeEvalStatus}
        openModal={openModal}
      />
      {data.candidateVersion && (
        <VersionCard
          identifier={`${data.name}@${data.candidateVersion}`}
          stamp="candidate · awaiting compile"
          commitHash={data.candidateCommit}
          author={data.candidateAuthor}
          diffSummary={data.candidateDiffSummary}
          compileStatus={data.candidateCompileStatus}
          evalStatus={data.candidateEvalStatus}
          actions={['view-diff', 'promote', 'discard']}
          openModal={openModal}
        />
      )}
    </section>
  );
}

// ---- PromptOpsBreadcrumb (JOBE-0165.3) ----
//
// Renders the "← Back to …" strip when the page was reached from W2 (Agent
// Detail) or W6 (PromptOps Fleet) via a drill-context fragment. Without a
// drill context, returns null (no strip).
function PromptOpsBreadcrumb() {
  const ctx = useDrillContext();
  const navigate = useNavigate();
  if (!ctx) return null;

  let label = null;
  let returnUrl = null;
  if (ctx.from === 'w2') {
    label = `← Back to Agent Detail · ${ctx.agentName ? toTitleCase(ctx.agentName) : 'Agent'}`;
    returnUrl = `/agent/${ctx.agentName || ''}`;
  } else if (ctx.from === 'w6') {
    label = '← Back to PromptOps Fleet';
    returnUrl = '/promptops/fleet';
  }
  if (!label) return null;

  return (
    <div className="promptops-drill-breadcrumb" role="navigation" aria-label="Return navigation">
      <button
        type="button"
        className="promptops-drill-back"
        onClick={() => clearDrillContext(() => navigate(returnUrl))}
      >
        {label}
      </button>
    </div>
  );
}

// ---- FailureClusterCard (JOBE-0165.3) ----
function FailureClusterCard({ cluster, isSignaled, openModal }) {
  const slug = slugify(cluster.cls);
  return (
    <Card
      className={`promptops-cluster-card${isSignaled ? ' promptops-cluster-card--signal' : ''}`}
      data-cluster-slug={slug}
    >
      <header className="promptops-cluster-head">
        <h3 className="promptops-cluster-title">{cluster.cls}</h3>
        <span className="promptops-cluster-count">{cluster.count} traces</span>
      </header>
      <section className="promptops-cluster-evidence">
        <h4>Evidence</h4>
        <table>
          <thead>
            <tr>
              <th>Trace</th>
              <th>Input</th>
              <th>Expected</th>
              <th>Actual</th>
            </tr>
          </thead>
          <tbody>
            {(cluster.evidence || []).map((e) => (
              <tr key={e.traceId}>
                <td><code>{e.traceId}</code></td>
                <td><code>{e.input}</code></td>
                <td><code>{e.expected}</code></td>
                <td><code>{e.actual}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="promptops-cluster-fix">
        <h4>Proposed fix</h4>
        <p>{cluster.proposedFix}</p>
      </section>
      <footer className="promptops-cluster-actions">
        <button
          type="button"
          onClick={() => openModal('apply-correction', { cls: cluster.cls, fix: cluster.proposedFix })}
        >
          Apply correction
        </button>
        <button
          type="button"
          onClick={() => openModal('open-trace', { traceId: cluster.evidence?.[0]?.traceId })}
        >
          Open trace in inspector
        </button>
        <button
          type="button"
          onClick={() => openModal('dismiss-cluster', { cls: cluster.cls })}
        >
          Dismiss cluster
        </button>
      </footer>
    </Card>
  );
}

// ---- FailureClusterSection (JOBE-0165.3) ----
function FailureClusterSection({ data, openModal }) {
  const [searchParams] = useSearchParams();
  const failureClassParam = searchParams.get('failureClass');
  const [signaledSlug, setSignaledSlug] = useState(null);

  useEffect(() => {
    if (!failureClassParam || !data?.failureClusters) return;
    const target = data.failureClusters.find((c) => slugify(c.cls) === failureClassParam);
    if (!target) return;

    let timeoutId;
    const rafId = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-cluster-slug="${failureClassParam}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      setSignaledSlug(failureClassParam);
      timeoutId = setTimeout(() => setSignaledSlug(null), 2500);
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [failureClassParam, data?.failureClusters]);

  if (!data?.failureClusters || data.failureClusters.length === 0) return null;
  return (
    <section className="promptops-cluster-section" aria-label="Failure clusters">
      {data.failureClusters.map((cluster) => (
        <FailureClusterCard
          key={cluster.cls}
          cluster={cluster}
          isSignaled={signaledSlug === slugify(cluster.cls)}
          openModal={openModal}
        />
      ))}
    </section>
  );
}

// ---- EvalResultsPanel (JOBE-0165.4) ----
function EvalResultsPanel({ runs }) {
  if (!runs || runs.length === 0) {
    return <section className="promptops-eval-empty">No eval runs yet.</section>;
  }
  const passRateHistory = runs.map((r) => r.passRate);
  return (
    <section className="promptops-eval-panel" aria-label="Eval results">
      <header className="promptops-eval-head">
        <h3>Eval results</h3>
        <Spark
          mode="absolute"
          data={passRateHistory}
          target={0.90}
          tone="ok"
          ariaLabel="Eval pass-rate trend"
        />
      </header>
      <table>
        <thead>
          <tr>
            <th>Run</th>
            <th>When</th>
            <th>Suite</th>
            <th>Pass rate</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.runId}>
              <td><code>{r.runId}</code></td>
              <td>{relativeTime(r.ts)}</td>
              <td><code>{r.suiteId}</code></td>
              <td>{(r.passRate * 100).toFixed(0)}%</td>
              <td>
                <StatusPill
                  tone={r.passRate >= 0.90 ? 'ok' : 'warn'}
                  label={r.passRate >= 0.90 ? 'pass' : 'warn'}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// ---- FixBundleSection (JOBE-0165.4) ----
function FixBundleSection({ clusters, openModal }) {
  if (!clusters || clusters.length === 0) return null;
  const bundle = computeBundle(clusters);
  return (
    <section className="promptops-bundle-section" aria-label="Candidate fix bundles">
      <h3>Candidate fix bundle</h3>
      <Card className="promptops-bundle-card">
        <header className="promptops-bundle-head">
          <span className="promptops-bundle-id">{bundle.id}</span>
          <span className="promptops-bundle-lift">
            +{(bundle.expectedLift * 100).toFixed(0)}% pass rate (expected)
          </span>
        </header>
        <ul className="promptops-bundle-clusters">
          {bundle.clusters.map((c) => (
            <li key={c.cls}>
              <code>{c.cls}</code> — {c.count} traces
            </li>
          ))}
        </ul>
        <footer className="promptops-bundle-actions">
          <button type="button" onClick={() => openModal('promote-bundle', { bundle })}>
            Promote bundle
          </button>
        </footer>
      </Card>
    </section>
  );
}

// ---- EvalResultsAndBundles (JOBE-0165.4) ----
function EvalResultsAndBundles({ data, openModal }) {
  if (!data) return null;
  return (
    <>
      <EvalResultsPanel runs={data.evalRuns} />
      <FixBundleSection clusters={data.failureClusters} openModal={openModal} />
    </>
  );
}

// ---- KeyboardShortcutBar (JOBE-0165.5) ----
//
// Fixed bottom strip listing the W5 power-user bindings. Visible discoverability
// for the same hotkeys the page-local keydown listener wires up below.
function ShortcutHint({ keys, label }) {
  return (
    <span className="promptops-shortcut">
      {keys.map((k, i) => (
        <kbd key={i} className="promptops-kbd" aria-label={`Key: ${k}`}>{k}</kbd>
      ))}
      <span className="promptops-shortcut-label">{label}</span>
    </span>
  );
}

function KeyboardShortcutBar() {
  return (
    <div
      className="promptops-shortcut-bar"
      role="complementary"
      aria-label="Keyboard shortcuts"
    >
      <ShortcutHint keys={['E']}     label="edit prompt" />
      <ShortcutHint keys={['B']}     label="create bundle" />
      <ShortcutHint keys={['↑', '↓']} label="navigate clusters" />
      <ShortcutHint keys={['?']}     label="help" />
    </div>
  );
}

// moveClusterFocus walks the cluster-card list and shifts document.activeElement
// to the next/previous card. Cards must carry tabIndex={-1} so they are
// focusable programmatically without joining the tab order.
function moveClusterFocus(direction) {
  const cards = Array.from(document.querySelectorAll('.promptops-cluster-card'));
  if (cards.length === 0) return;
  const current = document.activeElement;
  const idx = cards.indexOf(current);
  let nextIdx;
  if (idx === -1) {
    nextIdx = direction > 0 ? 0 : cards.length - 1;
  } else {
    nextIdx = Math.max(0, Math.min(cards.length - 1, idx + direction));
  }
  cards[nextIdx]?.focus();
}

// usePromptOpsHotkeys wires the page-local keydown listener. Listener is
// scoped to the page mount via the cleanup return so unmount → no leak.
function usePromptOpsHotkeys() {
  useEffect(() => {
    function onKeyDown(e) {
      // Skip when focus is in an input / textarea / contenteditable.
      const t = e.target;
      if (t && typeof t.matches === 'function'
          && t.matches('input, textarea, [contenteditable]')) {
        return;
      }
      // Modifier keys are reserved for shell-level chords (Cmd/Ctrl/Alt).
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        // Focus the candidate version card's first action button.
        document
          .querySelector('.promptops-version-card:last-of-type .promptops-version-actions button')
          ?.focus();
      } else if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        // Focus the bundle's primary action (Promote bundle).
        document.querySelector('.promptops-bundle-card button')?.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveClusterFocus(+1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveClusterFocus(-1);
      }
      // '?' is wired by the global OmniChordHelpSheet listener — no page-local handler.
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}

// ---- PromptOpsAgentPage ----
export function PromptOpsAgentPage() {
  const { name } = useParams();
  const overrides = useFlagStore((s) => s.overrides);
  const [data, setData] = useState(null);

  usePromptOpsHotkeys();

  // Modal state — shared across JOBE-0165.{2,3,4} action surfaces.
  // openModal(action, payload) sets the modal; closeModal clears it.
  const [modal, setModal] = useState(null);
  const openModal  = (action, payload) => setModal({ action, payload });
  const closeModal = () => setModal(null);

  useEffect(() => {
    const fixture = flaggedFetch('promptops.mock.agentDetail', overrides);
    if (fixture) {
      setData(fixture);
      return;
    }
    // TODO(JOBE-0165-followup): wire /api/promptops/agent/:name once the BFF
    // handler lands. Until then the empty state renders.
    setData(null);
    let cancelled = false;
    fetch(`/api/promptops/agent/${encodeURIComponent(name || '')}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => { if (!cancelled) setData(body); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [name, overrides]);

  // Identity head consumes the AgentDetail shape; map fixture fields → that shape.
  // nameSlug drives avatar color/initial; falls back to the URL :name when fixture is absent.
  const identityAgent = data
    ? {
        DisplayName: data.displayName,
        Roles: data.role ? [data.role] : [],
        Department: data.dept,
        Owner: data.owner,
        Phase: data.phase,
        nameSlug: data.name || name || '',
      }
    : { nameSlug: name || '' };

  return (
    <>
      <PromptOpsSubNav agentName={name} />
      <div className="promptops-agent-page" data-testid="promptops-agent-page" data-agent={name}>
        <PromptOpsBreadcrumb />
        <IdentityHead agent={identityAgent} />
        <QualityStrip data={data} />
        <PromptVersionStack data={data} openModal={openModal} />
        <FailureClusterSection data={data} openModal={openModal} />
        <EvalResultsAndBundles data={data} openModal={openModal} />
        <KeyboardShortcutBar />
      </div>
      {modal && (
        <ActionConfirmModal
          action={modal.action}
          payload={modal.payload}
          onClose={closeModal}
        />
      )}
    </>
  );
}

export default PromptOpsAgentPage;
